import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, map, catchError, of, throwError, first } from 'rxjs';
import { InsuranceTypesService, InsurenetInterfaceService } from '@core';
import { compareStates } from '../../utils/helper.functions';
import { isValidInsurant, validateStateForPriceRequest } from '../object-validators';
import { OrderQuery } from '../state/order.query';
import { OrderState } from '../models/order-state.model';
import { ProductsService } from 'app/products/products.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { Product } from '@models/product';
import { InsuranceType } from '@models/insurance-type';

@Injectable({
    providedIn: 'root',
})
export class PriceService {
    private orderState = toSignal(inject(OrderQuery).state$, { equal: compareStates });
    private insurenetInterface = inject(InsurenetInterfaceService);
    private insuranceTypesService = inject(InsuranceTypesService);

    private product = inject(ProductsService).product;

    private _price = signal(0);
    public readonly price = computed(() => this._price());

    public updatePrice(state: Partial<OrderState>): void {
        this.getPrice(this.filterInsurants({ ...this.orderState(), ...state }))
            .pipe(catchError(() => of(0)))
            .subscribe((newPrice) => {
                this._price.set(newPrice);
            });
    }

    public getPrice(state: OrderState): Observable<number> {
        if (!this.product()) {
            return throwError(() => new Error('Product is not set'));
        }
        let price$: Observable<number>;
        if (this.product().orderConfig.hasFlexPrice) {
            const errorMsg = validateStateForPriceRequest(this.product(), state);
            if (errorMsg) {
                return throwError(() => new Error(errorMsg));
            }
            price$ = this.getPriceFromBackend(state, this.product(), this.insuranceTypesService.insuranceType());
        } else {
            price$ = this.getStaticPrice();
        }
        return price$.pipe(first());
    }

    public resetPrice(): void {
        this._price.set(0);
        this.updatePrice({});
    }

    private filterInsurants(state: OrderState): OrderState {
        return {
            ...state,
            insurants: state.insurants.filter((insurant) => isValidInsurant(this.product(), insurant)),
        };
    }

    private getPriceFromBackend(state: OrderState, product: Product, insuranceType: InsuranceType): Observable<number> {
        return this.insurenetInterface.getPrice(state, product, insuranceType);
    }

    private getStaticPrice(): Observable<number> {
        return this.insuranceTypesService.insuranceType$.pipe(
            map((insuranceType) => insuranceType.pricePerSettlementPeriod),
        );
    }
}
